import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";
import { ActionPayloadValidationError, requireStringArrayField } from "@slot/client-engine";

/**
 * GAME-DEFINED action: "apply_win_multiplier" — the Cannon's wire protocol.
 *
 * A "win multiplier with a revealed queue": a multiplier applied to a win, plus the ordered queue
 * of upcoming multipliers the server has revealed. This game presents it as the cannon firing a
 * round at the grid. The win MONEY is NOT carried here — it rides on `grid_win_highlight`
 * (server pre-multiplied: `baseWinAmount` × `multiplierApplied` = `winAmount`); this action is
 * purely the multiplier + queue presentation state.
 *
 * Emitted once at spin reveal (loadout, `firedMultiplier` absent — no shot), then once per winning
 * cascade substep (authored AFTER that substep's `grid_win_highlight`s). Each action carries the
 * full post-state, so the client never diffs.
 *
 * Every multiplier (`chamberedMultiplier`, each `upcomingMultipliers` entry, `firedMultiplier` and
 * `totalMultiplier` when present) is a whole-number string, `/^\d+$/` — the backend's own payload schema
 * (decision 24). The rounds' font (`goldenNumbers1`) has only 0-9 and "x" and skips any other
 * character, so "2.5" would read "x25": anything else is a protocol error (the reload), never a
 * different number on the cannon (review 2026-09-27 #13). An optional field is absent when missing or
 * null; an empty string or a number is present, and refused, not read as a reveal.
 *
 * The type left the platform's canonical action set (game-defined now) — this handler registers
 * through the cannon feature's replay plugin (`CannonFeature`). AUTHORING
 * CONTRACT: backend + fixtures MUST carry `actionPhaseName: "win"` on the action (the platform's
 * type→phase resolver no longer knows it; unknown types default to "other", which super-fast skips
 * when flagged skippable — "win" always presents).
 */
export interface WinMultiplierInput {
  /**
   * The multiplier that fires on THIS substep's win (the bullet that just fired).
   * Absent on a reveal-only loadout (no shot yet).
   */
  firedMultiplier?: string;

  /** The multiplier now loaded (fires on the NEXT win). */
  chamberedMultiplier: string;

  /** The queue behind the chamber, in order (ascending). May be empty when depleted. */
  upcomingMultipliers: string[];
}

/** The slice of the game presentation the handler drives (`cannonMultiplierTarget`). */
export interface WinMultiplierPresentationTarget {
  presentWinMultiplier(input: WinMultiplierInput): Promise<void>;
}

/** Validates the payload + forwards to the game presentation (→ CannonPresenter → CannonView). */
export class ApplyWinMultiplierActionHandler implements RoundActionHandler {
  public readonly actionTypeName = "apply_win_multiplier";

  public constructor(private readonly target: WinMultiplierPresentationTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;

    // Every field is checked before the forward, so a refused payload draws nothing.
    const chamberedMultiplier = this.requireMultiplier("chamberedMultiplier", payload.chamberedMultiplier);
    const upcomingMultipliers = requireStringArrayField(this.actionTypeName, payload, "upcomingMultipliers").map(
      (value, i) => this.requireMultiplier(`upcomingMultipliers[${i}]`, value),
    );
    const firedMultiplier = this.optionalMultiplier(payload, "firedMultiplier");
    // Checked like the rest (decision 24) but not forwarded: this game does not show the running level.
    this.optionalMultiplier(payload, "totalMultiplier");

    await this.target.presentWinMultiplier({ firedMultiplier, chamberedMultiplier, upcomingMultipliers });
  }

  /** A multiplier: a whole-number string (see the action's contract above). */
  private requireMultiplier(fieldName: string, value: unknown): string {
    if (typeof value !== "string" || !/^\d+$/.test(value)) {
      throw new ActionPayloadValidationError(
        this.actionTypeName,
        fieldName,
        `Must be a whole-number string, got ${JSON.stringify(value)}.`,
      );
    }
    return value;
  }

  /** A multiplier the payload may leave out (missing or null); when present it follows `requireMultiplier`. */
  private optionalMultiplier(payload: Record<string, unknown>, fieldName: string): string | undefined {
    const value = payload[fieldName];
    if (value === undefined || value === null) return undefined;
    return this.requireMultiplier(fieldName, value);
  }
}
